import React, {Fragment, useCallback, useEffect} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import {GroupedVirtuoso, Virtuoso} from 'react-virtuoso'
import styled from '@emotion/styled'
import {DealFreezeEducationBanner} from 'components/DealFreezeEducationBanner'
import {BANNER_PLACEMENT} from 'components/DealFreezeEducationBanner/constants'
import {HotelCard} from 'components/HotelCard'
import {Pagination} from 'components/Pagination'
import {PartialMatchBanner} from 'components/PartialMatchBanner/PartialMatchBanner'
import {NoResults} from 'components/SearchUnavailability/NoResults'
import {Web2AppHoldOutWithExperimentMemo} from 'components/Web2AppHoldOutWrapper'
import {useIsSeoSRP} from 'hooks/useIsSeoSRP'
import {useSearchResultsBanner} from 'hooks/useSearchResultsBanner/useSearchResultsBanner'
import {trackTimingEvent} from 'middleware/analytics'
import {
  getIsAcquiringGeoCoordinates,
  getIsSyntheticTest
} from 'modules/meta/selectors'
import {setShowComparisonOffers} from 'modules/meta/slice'
import {getSearchType} from 'modules/sapiSearch/selectors'
import {
  getAnchorHotelId,
  getAnchorType,
  getGroupedHotelDetails,
  getHotelHasOffers,
  getHotelIdsToDisplay,
  getIsAnchorScenario,
  getIsFiltersApplied,
  getIsSearchCompleted,
  getIsSearchHasResults,
  getIsWaitingForHotelsData,
  getSearchId,
  getTotalCountOfResults,
  State
} from 'modules/sapiSearch/selectors'
import {AnchorType, Hotel} from 'modules/sapiSearch/slice'
import {SearchType} from 'modules/sapiSearch/utils'
import {
  getIsAnchorHotelHidden,
  getIsHomeSearch,
  getIsUserInteractedWithFilters
} from 'modules/search/selectors'

import {useDeviceLayout} from '@vio/atlas/context/deviceLayout'
import {GenericError} from '@vio/atlas/GenericError'
import {Text} from '@vio/atlas/Text'
import {cssTheme} from '@vio/atlas/themes'
import {linariaMq} from '@vio/atlas/utils/breakpoints'
import {
  trackSearchResultsPageFRP,
  trackSearchResultsPageMRP
} from '@vio/core/src/_web/utils/analytics/trackPerformanceMetrics'
import {Entity, Team} from '@vio/core/src/analytics/types/Events'
import FormattedMessageWrapper from '@vio/core/src/localization/components/FormattedMessage'

import {SearchResultsListItem} from './SearchResultsListItem'
import {SearchResultsTrustBanners} from './SearchResultsTrustBanners'

const LOADERS_COUNT = 10
const APP_INLINE_BANNER_INDEX = 4

const HeaderContainer = styled.div`
  padding: 0 0 ${cssTheme.layout.spacing.s500};
  ${linariaMq.desktopXs} {
    padding: 0 ${cssTheme.layout.spacing.s500} ${cssTheme.layout.spacing.s400};
  }
`

const NoResultsWrapper = styled.div`
  margin-top: 80px;
`

const Loaders: React.FC = () => {
  const loadingList: number[] = new Array(LOADERS_COUNT).fill(1)

  return (
    <>
      {loadingList.map((_, i) => (
        <HotelCard isLoading key={['HotelCardLoader', i].join('_')} />
      ))}
    </>
  )
}

export const SearchResultsList: React.FC = () => {
  const dispatch = useDispatch()
  const isSeoSRP = useIsSeoSRP()
  const hotelIds = useSelector(getHotelIdsToDisplay)
  const isSearchHasResults = useSelector(getIsSearchHasResults)
  const isWaitingForHotelsData = useSelector(getIsWaitingForHotelsData)
  const isFiltersApplied = useSelector(getIsFiltersApplied)
  const totalResultsCount = useSelector(getTotalCountOfResults)
  const searchIsHotelSearch = useSelector(getAnchorType) === AnchorType.hotel
  const anchorHotelId = useSelector(getAnchorHotelId)
  const anchorHotelHasOffers = useSelector((state: State) =>
    getHotelHasOffers(state, anchorHotelId)
  )
  const firstHotelHasOffers = useSelector((state: State) =>
    getHotelHasOffers(state, hotelIds[0])
  )
  const searchId = useSelector(getSearchId)
  const isAcquiringGeoCoordinates = useSelector(getIsAcquiringGeoCoordinates)
  const isSyntheticTest = useSelector(getIsSyntheticTest)

  const isHomeSearch = useSelector(getIsHomeSearch)
  const isSearchCompleted = useSelector(getIsSearchCompleted)
  const {isDesktop} = useDeviceLayout()
  const searchType = useSelector(getSearchType)

  const isUserInteractedWithFilters = useSelector(
    getIsUserInteractedWithFilters
  )

  const isAnchorHotelHidden = useSelector(getIsAnchorHotelHidden)

  const shouldEnableInlineBanner =
    searchType === SearchType.Place || searchType === SearchType.Hotel

  useEffect(() => {
    trackSearchResultsPageFRP(dispatch)

    if (searchId) {
      trackTimingEvent(
        dispatch,
        'Interaction',
        'search-start',
        isHomeSearch ? Entity.HomePageSearch : Entity.SearchResultsPageSearch,
        Team.Default,
        true
      )
    }
  }, [searchId, isHomeSearch, dispatch])

  const isAnchorScenario = Boolean(anchorHotelId)
  const isAnchorSearch = useSelector(getIsAnchorScenario)

  const {isBannerEnabled, onTrustSurveyVisibilityChange} =
    useSearchResultsBanner()

  useEffect(() => {
    const offerIsVisible = isAnchorScenario
      ? anchorHotelHasOffers
      : firstHotelHasOffers

    if (offerIsVisible || isSearchCompleted) {
      trackSearchResultsPageMRP(dispatch)
    }
  }, [
    isAnchorScenario,
    dispatch,
    anchorHotelHasOffers,
    firstHotelHasOffers,
    isSearchCompleted
  ])

  // eslint-disable-next-line no-warning-comments
  // TODO (@Search): Decide whether to keep or fix this bug
  // Emulating bug from the A side of SAPI A/B test by not showing messages for hotel searches.
  // It's up to Search sqd to decide what should be the correct behaviour
  const hasNoAvailableResults = !isSearchHasResults && !searchIsHotelSearch
  const shouldShowNoResultsMessage =
    totalResultsCount === 0 || (isFiltersApplied && hasNoAvailableResults)

  const {hasPartiallyMatched, greatDeals, groupCounts} = useSelector(
    getGroupedHotelDetails
  )

  const shouldShowPartialMatchBanner =
    hasPartiallyMatched && greatDeals.length <= 10

  const shouldShowOnlyPartialMatchBanner =
    searchIsHotelSearch &&
    isAnchorHotelHidden &&
    !isSearchHasResults &&
    isUserInteractedWithFilters

  useEffect(() => {
    dispatch(setShowComparisonOffers({showComparisonOffers: true}))
  }, [dispatch])

  const renderHotelItem = useCallback(
    (index: number) => {
      const hotelId: Hotel['objectID'] = hotelIds[index]
      const calculatedIndex = isAnchorScenario ? index + 1 : index
      const withDealFreezeEducationBanner = !isAnchorSearch && index === 0
      const withTrustpilotBanner = isBannerEnabled(
        'trustpilot',
        calculatedIndex
      )
      const withSurveyBanner = isBannerEnabled('trustSurvey', calculatedIndex)

      return (
        <React.Fragment key={hotelId}>
          {calculatedIndex === APP_INLINE_BANNER_INDEX && (
            <Web2AppHoldOutWithExperimentMemo
              holdOutElement={null}
              experimentId="c4684a10-feature-inline-banner-app-acquisition"
              elementA={null}
              enabled={shouldEnableInlineBanner}
              elementB={null}
            />
          )}
          <SearchResultsListItem
            index={calculatedIndex}
            hotelId={hotelId}
            shouldShowDivider={!withSurveyBanner}
          />
          {withDealFreezeEducationBanner && (
            <DealFreezeEducationBanner slot={BANNER_PLACEMENT.secondary} />
          )}
          {(withSurveyBanner || withTrustpilotBanner) && (
            <SearchResultsTrustBanners
              onTrustSurveyVisibilityChange={onTrustSurveyVisibilityChange}
            />
          )}
        </React.Fragment>
      )
    },
    [
      hotelIds,
      isAnchorScenario,
      isAnchorSearch,
      isBannerEnabled,
      shouldEnableInlineBanner,
      onTrustSurveyVisibilityChange
    ]
  )

  const renderGroupHeader = (index: number): React.ReactNode => {
    // Hide the great deal header when we don't have any great Deals
    if (greatDeals.length === 0 && index === 0) return null
    // Hide the great deal header when we don't have anchor hotel
    if (!isAnchorScenario && index === 0) return null
    return (
      <>
        {index === 1 && shouldShowPartialMatchBanner && (
          <PartialMatchBanner numResults={greatDeals.length} />
        )}
        {isDesktop && index === 1 && (
          <HeaderContainer>
            <Text as="h1" variant="titleL">
              <FormattedMessageWrapper
                id="partialResultsHeading"
                defaultMessage="Results that match some of your criteria"
              />
            </Text>
          </HeaderContainer>
        )}
      </>
    )
  }

  if (isAcquiringGeoCoordinates || isWaitingForHotelsData) return <Loaders />

  if (shouldShowOnlyPartialMatchBanner)
    return (
      <div>
        <PartialMatchBanner numResults={0} />
      </div>
    )

  if (shouldShowNoResultsMessage || hasNoAvailableResults)
    return (
      <NoResultsWrapper>
        <GenericError>
          <NoResults />
        </GenericError>
      </NoResultsWrapper>
    )

  if (hasPartiallyMatched) {
    return (
      <Pagination>
        <GroupedVirtuoso
          useWindowScroll
          initialItemCount={Math.min(1, hotelIds.length)}
          style={{minHeight: '100vh'}}
          groupCounts={groupCounts}
          increaseViewportBy={300}
          components={{
            TopItemList: Fragment
          }}
          groupContent={groupIndex => renderGroupHeader(groupIndex)}
          itemContent={index => renderHotelItem(index)}
        />
      </Pagination>
    )
  }

  return (
    <Pagination>
      {isSeoSRP || isSyntheticTest ? (
        hotelIds.map((_, index) => renderHotelItem(index))
      ) : (
        <Virtuoso
          // initialItemCount prevents the flash of white space when the cards load
          initialItemCount={Math.min(3, hotelIds.length)}
          style={{minHeight: '100vh'}}
          useWindowScroll
          data={hotelIds}
          increaseViewportBy={300}
          itemContent={index => renderHotelItem(index)}
        />
      )}
    </Pagination>
  )
}
